import restService from "../rest-service/RestService";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { DRCXConfigurationModel } from "../../common/models/drcx/DRCXConfigurationModel";
import { IDrcxEffectiveTimeModel } from "../../common/models/drcx/DRCXEffectiveTimeModel";
import { IDowloadFileStoreQueryParams, IGetConfigurationListOptions, IGetReportQueryParams, IReportConfigurationDataDTO, IReportExportOptions, IReportExportResultsModel, IReportModel, IRunReportAdHocOptions, IScheduledReportOptions, ReportConfigurationDataModel } from "./Models";
import { ExportFormatTypeEnum, IAvailableNotificationInfoDTO, IDeleteDeliveryModel, IEditReportTaskDTO, IReportTaskSummaryDTO, IScheduledReportRestQueryParams, ISendReportModel, ReportDeliveryType, ReportTypeEnum, devInfoEmail } from "./ReportScheduleModels";
import { DateRangeType } from "../../common/enums/DRCXEnums";
import i18n from "../localization-service/i18n";

const ReportingDataService = {


    getConfigurations: async function (
        groupID: number,
        isMyReports?: boolean,
        includeInaccessibleReports?: boolean,
        includeChildGroups?: boolean,
        signal?: AbortSignal
    ): Promise<ReportConfigurationDataModel> {

        const path: string = `v2/groups/${groupID}/reportlist`;

        const options = isMyReports
            ? {
                isMyReports: isMyReports,
                showAllConfigs: includeInaccessibleReports,
                includeChildGroups: includeChildGroups
            }
            : includeChildGroups
                ? {
                    includeChildGroups: includeChildGroups
                }
                : undefined;

        const result = await restService.get<IReportConfigurationDataDTO, IGetConfigurationListOptions>(path, signal, options);

        return new ReportConfigurationDataModel(result.data);
    },

    getEffectiveDates: async function (
        contextGroupID: number,
        configurationID: number,
        signal?: AbortSignal
    ): Promise<IDrcxEffectiveTimeModel> {

        const path: string = `groups/${contextGroupID}/reports/${configurationID}/effectivedates`;

        const result = await restService.get<IDrcxEffectiveTimeModel>(path, signal);

        return result.data;
    },

    getReport: async function (
        contextGroupID: number,
        configurationID: number,
        disableDeferredData: boolean,
        beginDate?: Date,
        endDate?: Date,
        signal?: AbortSignal
    ): Promise<IReportModel> {

        const path: string = `groups/${contextGroupID}/reports/${configurationID}`;

        const getQueryParams: IGetReportQueryParams = {
            beginDate: beginDate,
            disableDeferredData: disableDeferredData,
            endDate: endDate
        };

        const result = await restService.get<IReportModel, IGetReportQueryParams>(path, signal, getQueryParams);

        return result.data;
    },

    getReportDeferredData: async function (
        contextGroupID: number,
        filestoreID: number,
        signal?: AbortSignal
    ): Promise<IReportModel> {

        const path: string = `groups/${contextGroupID}/reports/filestore/data`;

        const getQueryParams: IDowloadFileStoreQueryParams = {
            filestoreID: filestoreID
        };

        const result = await restService.get<IReportModel, IDowloadFileStoreQueryParams>(path, signal, getQueryParams);

        return result.data;
    },

    exportReport: async function (
        groupId: number,
        reportId: number,
        mimeType: ApplicationMimeTypesEnum,
        notificationKey: string,
        beginDate?: Date,
        endDate?: Date,
        signal?: AbortSignal
    ): Promise<IReportExportResultsModel> {

        const path: string = `groups/${groupId}/reports/${reportId}/export`;

        const reportExportOption: IReportExportOptions = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            beginDate: beginDate,
            endDate: endDate
        } as IReportExportOptions;

        const result = await restService.post<IReportExportOptions, IReportExportResultsModel>(path, reportExportOption, signal);

        return result.data;
    },

    runReportAdhoc: async function (
        groupID: number,
        report: DRCXConfigurationModel,
        disableDeferredData: boolean,
        signal?: AbortSignal
    ): Promise<IReportModel> {

        const path: string = `v2/groups/${groupID}/reports/adhoc`;

        // make this params type        
        const dto: IRunReportAdHocOptions =
        {
            configuration: report.toDTO(),
            disableDeferredData: disableDeferredData
        };

        const response = await restService.post<IRunReportAdHocOptions, IReportModel>(path, dto, signal);

        return response.data;
    },

    saveReportTask: async function (groupID: number, model: IEditReportTaskDTO): Promise<IEditReportTaskDTO> {
        const path = `groups/${groupID}/reporttask`;
        const response = await restService.post<IEditReportTaskDTO, IEditReportTaskDTO>(path, model);

        return response.data;
    },

    sendReport: async function(groupID: number,
        reportID: number,
        mimeType: ApplicationMimeTypesEnum, 
        includeSubGroups: boolean, 
        beginDate?: Date, 
        endDate?: Date
    ): Promise<void> {
        const path = `groups/${groupID}/reports/${reportID}/sendreport`;

        const sendReportModel: ISendReportModel = {
            configurationId: reportID,
            startDate: beginDate,
            endDate: endDate,
            includeSubGroups: includeSubGroups,
            documentType: mimeType
        } as ISendReportModel;

        await restService.post<ISendReportModel, void>(path, sendReportModel);
    },

    // getScheduledReports(groupID: number): Promise<Array<IScheduledReportSummaryDTO>> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reportschedules"
    //     ].join("/");

    //     const queryParams = {
    //         includeAdvancedIntervals: false,
    //         includeInactiveSchedules: true
    //     } as IScheduledReportRestQueryParams;

    //     return this._restService
    //         .post(path, queryParams)
    //         .then((data: IRestResponse<Array<IScheduledReportSummaryDTO>>) => {
    //             return data.entity;
    //         });
    // },

    // getMyReportTaskList(groupID: number): Promise<Array<IReportTaskSummaryDTO>> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "myreporttasks"
    //     ].join("/");

    //     const queryParams = {
    //         includeAdvancedIntervals: true,
    //         includeInactiveSchedules: true
    //     } as IScheduledReportRestQueryParams;

    //     return this._restService
    //         .post(path, queryParams)
    //         .then((data: IRestResponse<Array<IReportTaskSummaryDTO>>) => {
    //             return data.entity;
    //         });
    // },

    getReportTaskList: async function(organizationID: number, 
        groupID: number, 
        myReports: boolean,
        signal?: AbortSignal): Promise<IReportTaskSummaryDTO[]> {


        const path: string = !myReports ? 
            `groups/${groupID}/reporttasks` :
            `groups/${groupID}/myreporttasks`;

        const queryParams = {
            includeAdvancedIntervals: true, //changed from false to allow repeat intervals > 1
            includeInactiveSchedules: true,
            organizationID: myReports ? undefined : organizationID
        } as IScheduledReportRestQueryParams;


        const response = await restService.post<IScheduledReportRestQueryParams, IReportTaskSummaryDTO[]>(path, queryParams, signal);

        return response.data;
    },
    getReportTask: async function(groupID: number, 
        taskID: number,
        myReports: boolean,
        signal?: AbortSignal): Promise<IEditReportTaskDTO> {


        const path: string = `groups/${groupID}/reporttasks/${taskID}`;

        const queryParams = {
            isMyReports: myReports,
        } as IScheduledReportRestQueryParams;


        if (taskID > 0) {
            const response = await restService.get<IEditReportTaskDTO, IScheduledReportRestQueryParams>(path, signal, queryParams);

            return response.data;
        } else {


            const now = new Date();

            return {

                id: -1,
                name: '',
                reportConfigId: -1,
                exportFormat: ExportFormatTypeEnum.Excel,
                reportType: ReportTypeEnum.User,
                organizationId: -1,
                timezoneIndex: 20,
                description: '',
                deliveries: [{
                    id: -1,
                    address: devInfoEmail,
                    deliveryType: ReportDeliveryType.Email,
                    selected: true
                }],
                schedules: [{
                    id: -1,
                    description: i18n.t("scheduledReports.newSchedule", {num: 1}) as string,
                    timezoneIndex: 20,
                    repeatRange: DateRangeType.Month,
                    repeatInterval: 1,
                    lastRunTime: undefined,
                    dayOfMonth: now.getDate(),
                    dayOfWeek: now.getDay(),
                    hour: now.getHours(),
                    minute: now.getMinutes(),
                    isActive: true,
                    startTime: now,
                }],

                

            } as IEditReportTaskDTO;
        }
    },
    getAvailableReportNotifications: async function(groupID: number, 
        signal?: AbortSignal): Promise<IAvailableNotificationInfoDTO> {

        const path: string = `groups/${groupID}/report-schedules/notifications`;

        const response = await restService.get<IAvailableNotificationInfoDTO>(path, signal);

        return response.data;
    },

    deleteReportTask: async function(groupID: number, taskID: number, softDelete?: boolean): Promise<void> {

        const path = `groups/${groupID}/reporttasks/${taskID}`;

            const queryParams: IGetReportQueryParams = {
                softDelete: softDelete ? true : false
            };

            await restService.delete<void, IGetReportQueryParams>(path, undefined, queryParams)
    },
    deleteReportDeliveries: async function(groupID: number, taskID: number, ids: number[]): Promise<void> {

        const model: IDeleteDeliveryModel = {
            deliveryIDs: ids
        };

        const path = `groups/${groupID}/reporttasks/${taskID}/deliveries`;
        await restService.delete<IDeleteDeliveryModel, void >(path, undefined, undefined, model);
    },    
    // deleteReportDeliveries(groupID: number, taskID: number, deliveryIds: Array<number>):Promise<any> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reporttasks",
    //         taskID,
    //         "deliveries"
    //     ].join("/");

    //     const model = {
    //         DeliveryIDs: deliveryIds
    //     };

    //     return this._restService
    //         .delete<{ deliveryIDs: Array<number> }, void>(path, null, model)
    //         .then((data) => {
    //             return data.entity;
    //         });
    // },


    // getReportTask(groupID: number, taskID: number, isMyReports: boolean): Promise<EditReportTaskModel> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reporttasks",
    //         taskID
    //     ].join("/");

    //     const qp = isMyReports
    //         ? {
    //             isMyReports: isMyReports
    //         }
    //         : null;

    //     return this._restService
    //         .get(path, qp)
    //         .then((data: IRestResponse<IEditReportTaskDTO>) => {
    //             return new EditReportTaskModel(data.entity);
    //         });
    // },

    // getScheduledReportAvailableNotificationInfo(groupID: number): Promise<AvailableNotificationInfo> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "report-schedule",
    //         "available-notification-info"
    //     ].join("/");

    //     return this._restService.get<IAvailableNotificationInfoDTO>(path)
    //         .then((data: IRestResponse<IAvailableNotificationInfoDTO>) => {
    //             return new AvailableNotificationInfo(data.entity);
    //         });
    // },

    // deleteReportTask(groupID: number, taskID: number, softDelete?: boolean): Promise<any> {
    //     if (taskID > 0) {
    //         const path: string = [
    //             "groups",
    //             groupID,
    //             "reporttasks",
    //             taskID
    //         ].join("/");

    //         const queryParams = {
    //             softDelete: softDelete ? true : false
    //         };

    //         return this._restService
    //             .delete(path, queryParams)
    //             .then((data) => { return data.entity });
    //     }
    // },

    // saveReportTask(groupID: number, model: EditReportTaskModel): Promise<EditReportTaskModel> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reporttask",
    //     ].join("/");

    //     return this._restService
    //         .post(path, model.toDTO())
    //         .then((data: IRestResponse<EditReportTaskModel>) => {
    //             return new EditReportTaskModel(data.entity);
    //         });
    // },

    // isUniqueReportTaskName(groupID: number, name: string): Promise<boolean> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reporttask",
    //         "isuniquename"
    //     ].join("/");

    //     const qp = {
    //         name: name
    //     };

    //     return this._restService
    //         .get(path, qp)
    //         .then((data: IRestResponse<boolean>) => {
    //             return data.entity;
    //         });
    // },

    // getCurrentUserRecipients(groupID: number): Promise<Array<ISelectableItem>> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "report-schedule",
    //         "user-recipients"
    //     ].join("/");

    //     return this._restService
    //         .get(path)
    //         .then((data: IRestResponse<Array<ISelectableItem>>) => {
    //             return data.entity;
    //         });
    // },

    // deleteReportDeliveries(groupID: number, taskID: number, deliveryIds: Array<number>):Promise<any> {
    //     const path: string = [
    //         "groups",
    //         groupID,
    //         "reporttasks",
    //         taskID,
    //         "deliveries"
    //     ].join("/");

    //     const model = {
    //         DeliveryIDs: deliveryIds
    //     };

    //     return this._restService
    //         .delete<{ deliveryIDs: Array<number> }, void>(path, null, model)
    //         .then((data) => {
    //             return data.entity;
    //         });
    // },
    runScheduledReport: async function (reportID: number, options: IScheduledReportOptions): Promise<void> {
        const path: string = `scheduledreports/${reportID}/run`;
        await restService.post<IScheduledReportOptions, void>(path, options);
    },    
};

export default ReportingDataService;